import { FC, useCallback, useContext, useState } from "react";
import { AdminDisableUserResult } from "tech-health-assessment-sdk/dist";
import { SecondaryButton, WarnButton } from "../../../../../../../../common/components/buttons/buttons";
import { CalloutCard } from "../../../../../../../../common/components/callout-card/callout-card";
import { Dialog, DialogProps } from "../../../../../../../../common/components/dialog/dialog";
import { DialogCard, DialogFooter, DialogHeader, DialogTitle } from "../../../../../../../../common/components/dialog/dialog.styled";
import { LoadingSpinner } from "../../../../../../../../common/components/loading-spinner/loading-spinner";
import { SnackbarContext } from "../../../../../../../../common/components/snackbar-provider/snackbar-provider";
import { StandardDialogInnerContainer } from "../../../../../../../../common/components/standard-dialog-inner-container/standard-dialog-inner-container";
import { AppColors } from "../../../../../../../../common/constants/colors/app-colors";
import { service } from "./disable-user-dialog.service";

type DisableUserDialogProps = {
    userId: string
    onRefresh: () => void
} & Omit<DialogProps, 'children'>

export const DisableUserDialog: FC<DisableUserDialogProps> = ({
    userId,
    isOpen,
    onClose,
    onRefresh,
}) => {

    // context
    const { showSnackbar } = useContext(SnackbarContext)

    // state
    const [isLoading, setIsLoading] = useState<boolean>(false)

    // event handlers
    const handleDisableUserClick = useCallback(async () => {
        setIsLoading(true)

        let result: AdminDisableUserResult | undefined = undefined;
        let error: unknown;
        try {
            result = await service.disableUser(userId)
        } catch (err) {
            error = err;
        }

        if (!!error || !result?.success) {
            showSnackbar({
                type: 'error',
                title: 'Something went wrong',
                message: 'If the issue persists, please contact support.'
            });
        } else {
            showSnackbar({
                type: 'success',
                title: 'Successfully disabled user',
            })
        }

        onClose()
        onRefresh()
        setIsLoading(false)
    }, [onClose, onRefresh, showSnackbar, userId])

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
        >
            <DialogCard isOpen={isOpen}>
                <StandardDialogInnerContainer>
                    <DialogHeader>
                        <DialogTitle>
                            Disable User?
                        </DialogTitle>
                    </DialogHeader>
                    <CalloutCard type='warning'>
                        <h2>What will happen?</h2>
                        <br />
                        <span>If you proceed...</span>
                        <ul>
                            <li>The user will no longer be able to log in.</li>
                            <li>We will not tell the user that you've disabled their account.</li>
                            <li>You will still be able to access all of the user's data - including assessments.</li>
                            <li>You can re-enable the user's account at any time</li>
                        </ul>
                    </CalloutCard>
                    <DialogFooter>
                        <SecondaryButton
                            disabled={isLoading}
                            onClick={onClose}
                        >
                            Cancel
                        </SecondaryButton>
                        <WarnButton
                            disabled={isLoading}
                            onClick={handleDisableUserClick}
                        >
                            {isLoading && (
                                <LoadingSpinner
                                    size={14}
                                    color={AppColors.text.onDarkBackground}
                                />
                            )}
                            Disable User
                        </WarnButton>
                    </DialogFooter>
                </StandardDialogInnerContainer>
            </DialogCard>
        </Dialog>
    )
}